/**
 * Copied from https://github.com/lindenlab/extraction-tools/blob/master/web/src/components/Money/createCurrencyFormatter.js
 *
 * Converted BigInt types to standard Number for broader browser compatability, but left in BigInt formatting for future support and Tools parity.
 */

import { CurrencyDisplay } from "./types.js";

function assertInt(num) {
  if (Number.isInteger(num)) {
    return true;
  }
  throw new Error("Error: Cannot use numbers with decimal points.");
}

/**
 * Converts the value to a Number if necessary.
 *
 * @param {any} value
 * @returns {number}
 * @throws {Error} if the value is not a number, or string, or if the
 *   number or string value is not integral
 */
function ensureInt(value) {
  switch (typeof value) {
    case "number":
      assertInt(value);
      return value;
    case "bigint":
    case "string":
      const numValue = Number(value);
      assertInt(numValue)
      return numValue
    default:
      throw new Error(`Error: Invalid type (${typeof value}) for value`);
  }
}

/**
 * Some virtual currencies use one value for fractional precision (minorUnit), but
 * round to a smaller number of digits for display (displayMinorUnit). Returns the
 * rounded integerPart and fractionPart values.
 *
 * @param {number} value Absolute value
 * @param {number} minorUnit
 * @param {number} displayMinorUnit
 * @returns {Object<{integerPart: number, fractionPart: number}>}
 */
function roundNumberValue(value, minorUnit, displayMinorUnit) {
  let integerPart = Math.floor(value / Math.pow(10, minorUnit));

  // truncate the fraction part to the display unit length
  const truncateDigits = minorUnit - displayMinorUnit;
  let fractionPart =
    Math.floor(value / Math.pow(10, truncateDigits)) % Math.pow(10, displayMinorUnit);

  // retrieve the digit just after the truncated digit
  const roundingDigit = Math.floor(value / Math.pow(10, truncateDigits - 1)) % 10;

  // Tilia uses the "away from zero" rounding rule, which means that positive numbers
  // round up and negative numbers round down. Rounding up is equivalent to away from
  // zero in this function because it is called with an absolute value.
  if (roundingDigit >= 5) {
    if (displayMinorUnit > 0) {
      fractionPart++;
    } else {
      integerPart++;
    }
  }
  // round down case is handled inherently due to truncation

  return { integerPart, fractionPart };
}

/**
 * Returns a formatter function which accepts a Tilia currency value (the value in the
 * lowest-common denomination of that currency, for example cents for "USD") as a Number,
 * Number, or string, and returns a formatted string according to the specified locale.
 *
 * @param {string} locale
 * @param {string} currency
 * @param {CurrencyDisplay} currencyDisplay
 * @param {CurrencyInfo} currencyInfo
 * @returns {function(value: Number|string): string}
 */
function createCurrencyFormatter(
  locale,
  currency,
  currencyDisplay = CurrencyDisplay.Symbol,
  currencyInfo
) {
  let minorUnit; // fraction digits
  let displayMinorUnit; // fraction digits for display
  let formatter;
  let virtualCurrencySymbol = undefined;

  if (Object.prototype.hasOwnProperty.call(currencyInfo, currency)) {
    const virtualCurrency = currencyInfo[currency];

    // The settings values always presume US-English locale for API responses, and use basic string
    // concatenation for formatting, so the defined symbol sometimes includes a trailing space.
    // Strip it here for proper localized handling. Use the currency code as the symbol if one is
    // not configured.
    virtualCurrencySymbol = virtualCurrency.symbol?.trim() || virtualCurrency.currency;

    // TODO: New tests here
    // If the requested currencyDisplay is to use a symbol, but the virtual currency's "symbol" is
    // the same as its currency code, switch the currencyDisplay to CurrencyDisplay.Code instead
    // for proper localized handling of spacing.
    if (
      (currencyDisplay === CurrencyDisplay.Symbol ||
        currencyDisplay === CurrencyDisplay.NarrowSymbol) &&
      virtualCurrencySymbol === virtualCurrency.currency
    ) {
      currencyDisplay = CurrencyDisplay.Code;
    }

    minorUnit = virtualCurrency.minor_unit;
    displayMinorUnit = virtualCurrency.display_minor_unit ?? virtualCurrency.minor_unit;
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      signDisplay: "always", // workaround for BigInt and negative zero; see below
      currency: "USD", // use USD to get the correct localized number format; symbol is swapped below
      currencyDisplay,
      minimumFractionDigits: displayMinorUnit,
      maximumFractionDigits: displayMinorUnit,
    });

    switch (currencyDisplay) {
      case CurrencyDisplay.Symbol: // no distinction between these two for Tilia virtual currencies
      case CurrencyDisplay.NarrowSymbol:
        break;

      case CurrencyDisplay.Name:
        // no current equivalent (i.e., "US dollars") in settings; use currency code for now
        virtualCurrencySymbol = virtualCurrency.currency;
        break;

      case CurrencyDisplay.Code:
      default:
        virtualCurrencySymbol = virtualCurrency.currency;
    }
  } else {
    // Fiat currency, or unknown virtual currency: use the formatting settings from the Intl library.
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      signDisplay: "always",
      currency,
      currencyDisplay,
    });
    const options = formatter.resolvedOptions();
    minorUnit = options.minimumFractionDigits;
    displayMinorUnit = options.minimumFractionDigits;
  }

  return (value) => {
    value = ensureInt(value);

    if (value > Number.MAX_SAFE_INTEGER) {
      throw new Error("Error: value is greater than than Number.MAX_SAFE_INTEGER");
    }

    const isNegative = value < 0;
    if (isNegative) {
      value *= -1;
    }

    let integerPart;
    let fractionPart;

    if (displayMinorUnit < minorUnit) {
      const roundedValue = roundNumberValue(value, minorUnit, displayMinorUnit);
      integerPart = roundedValue.integerPart;
      fractionPart = roundedValue.fractionPart;
    } else {
      const fractionDivisor = Math.pow(10, minorUnit);
      integerPart = value / fractionDivisor;
      fractionPart = value % fractionDivisor;
    }

    if (isNegative) {
      integerPart *= -1;
    }

    // This adds extra formatting for BigInt cases. We are currently not supporting BigInt, but leaving this (from Tools) in for future BigInt support.
    // Only the integer part is initially formatted to ensure the value is always interpreted
    // as a BigInt (some implementations will interpret strings as lossy Number values). Because
    // we configure the formatter with minimumFractionDigits, there will always be a formatted
    // fractional part, all zeros. We format to parts to easily swap out this fractional part
    // and set the appropriate currency symbol when needed for a Tilia virtual currency, then
    // re-assemble into a contiguous string.

    return formatter
      .formatToParts(integerPart)
      .map(({ type, value }) => {
        switch (type) {
          case "plusSign":
            // NumberFormat is configured to always include the sign to work around a limitation
            // where BigInt doesn't have a negative zero, so the integer part may lose its sign
            // for values that are smaller than one unit
            return isNegative ? "-" : "";
          case "fraction":
            return fractionPart.toString().padStart(displayMinorUnit, "0");
          case "currency":
            return virtualCurrencySymbol ?? value;
          default:
            return value;
        }
      })
      .join("");
  };
}

export default createCurrencyFormatter;
